/**
 * Billing the estimate.
 *
 * The builder has always been able to ask somebody to buy. This is the other
 * half: a document that asks them to pay, as a PDF, because the people who pay
 * these — a carrier, a general contractor, a property manager — pay off an
 * attachment rather than a link. See ADR 0068.
 *
 * Three things this screen deliberately does not let anybody do:
 *
 *  - **Edit a figure.** Every amount comes off the estimate through
 *    `buildInvoice` in packages/core, and its total is the contract amount the
 *    ladder works out — pinned by a test. A bill somebody can retype is a bill
 *    that stops agreeing with the job it came from, and the disagreement shows
 *    up as an argument with a customer months later.
 *  - **Choose the number.** The database takes the next one, under a lock, and
 *    it is the job number — which is what QuickBooks carries too, so a payment
 *    can be matched to the bill that asked for it.
 *  - **Bill part of an estimate.** This invoices an estimate, all of it.
 *    Billing a deposit or a stage is the payment schedule's job.
 *
 * Who it goes to *is* editable, and has to be. The invoice Jonathon sent as the
 * example was addressed to an insurance adjuster with the homeowner's name in
 * the memo, which is the normal shape of a supplement.
 *
 * ## Which estimates
 *
 * A job priced three ways carries three estimates, and a customer can sign for
 * more than one — Katie Harding-Smith signed all four options on job 1015.
 * Until 2026-09-22 this screen could only see the estimate whose tab happened
 * to be open, so billing that job meant four separate invoices and four
 * separate numbers. Jonathon: "when we click on create invoice and there's
 * multiple estimates there needs to be an option to select which invoices
 * we'd like to create."
 *
 * So the estimates are a list of tick boxes, and every ticked one goes on the
 * bill under its own heading with its own discount and its own general
 * contractor fee — `invoiceGroupLines` in packages/core. What starts ticked is
 * what the job is actually selling: every active estimate when the open one is
 * active, and otherwise just the open one, so pressing Invoice on an option
 * nobody chose still bills that option and nothing else.
 *
 * ## Received and due
 *
 * Also his, the same day: it "needs to show the amount received and the
 * remaining due". Both figures are `jobBalance` in packages/core, untouched —
 * the one answer in Roof Board to how much is owed (CLAUDE.md rule 5). They
 * are the *job's*, so a bill for one option of four can ask for less than the
 * balance it prints; the document says so in words rather than showing a
 * customer a smaller debt than they have.
 */

import {
  buildInvoice,
  invoiceAddressLines,
  invoiceLines,
  invoiceTotal,
  invoiceBlocker,
  invoiceCustomerName,
  invoiceDueDate,
  invoiceFilename,
  invoiceMoney,
  invoiceNumber,
  invoiceQty,
  jobAddress,
  type Balance,
  type ChangeOrder,
  type Estimate,
  type EstimateItem,
  type InvoiceGroup,
  type InvoiceSnapshot,
  type Job,
} from "@roof-hawk/core";
import { Fragment, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useGmailAccount } from "../email/useGmailAccount";
import { fmtDate, fmtDateOnly } from "../../lib/format";
import { sendEmail } from "../../lib/gmail";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";
import { downloadInvoicePdf, invoicePdfBase64 } from "../../lib/invoicePdf";
import {
  createInvoice,
  fetchAutoExpenseAdjustments,
  fetchCompanySettings,
  fetchInvoices,
  fetchInvoiceSources,
  fetchJobBalance,
  fileEmailedPdf,
  markInvoiceSent,
  voidInvoice,
  type InvoiceRow,
} from "../../lib/queries";

/** The core's reason a bill cannot be made, in the app's language. */
function blockerOf(draft: InvoiceSnapshot): string | null {
  const reason = invoiceBlocker(draft);
  return reason == null ? null : t(reason);
}

/** Today, as an ISO date in the reader's own timezone. */
function today(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

export function InvoiceModal({
  job,
  estimate,
  estimates,
  labels,
  items,
  changeOrders,
  onClose,
}: {
  job: Job;
  /** The estimate whose tab was open when Invoice was pressed. */
  estimate: Estimate;
  /** Every estimate on the job, so more than one of them can be billed. */
  estimates?: readonly Estimate[];
  /** What the builder's own tabs call them — `estimateOptionLabels`. */
  labels?: ReadonlyMap<string, string>;
  items: readonly EstimateItem[];
  changeOrders: readonly ChangeOrder[];
  onClose: () => void;
}) {
  const settings = useAsync(() => fetchCompanySettings(), []);
  const invoices = useAsync(() => fetchInvoices(job.id), [job.id]);
  // The GC fee is billed on at cost, so the bill has to charge the figure the
  // job carries rather than recomputing the rule. Once somebody can correct
  // that figure or strike it off (see `applyRemovals`), an invoice that worked
  // it out for itself would bill an amount nobody agreed to.
  const adjustments = useAsync(
    () => fetchAutoExpenseAdjustments(estimate.id),
    [estimate.id],
  );
  /*
   * What has arrived and what is left, for the panel and for the document.
   *
   * `fetchJobBalance` and nothing else: the balance card, the production board
   * and the letter that chases the money all read the same function, and a
   * bill that worked the figure out for itself is the second answer Jonathon
   * asked on 2026-09-08 never to have again.
   */
  const balanceQuery = useAsync(() => fetchJobBalance(job), [job.id]);
  const balance: Balance | null = balanceQuery.data ?? null;

  /*
   * The estimates this bill covers.
   *
   * Every active one when the open estimate is active — a multi-option job can
   * have several, and that set is exactly what `invoiceAmount` adds up to work
   * out what the customer owes. When the open estimate is not active it is an
   * option nobody chose, and pressing Invoice on it means that option.
   */
  const all = estimates?.length ? estimates : [estimate];
  const [chosenIds, setChosenIds] = useState<readonly string[]>(() => {
    const active = all.filter((e) => e.is_active).map((e) => e.id);
    return estimate.is_active && active.length ? active : [estimate.id];
  });
  const chosen = all.filter((e) => chosenIds.includes(e.id));

  /*
   * The lines and change orders of every estimate on the job, ticked or not,
   * so the column beside each box says what ticking it would add. Three reads
   * for the lot of them (`fetchInvoiceSources`), and the open estimate already
   * arrived as props, so a job with one estimate draws its bill before this
   * comes back and never blinks.
   */
  const sources = useAsync(
    () => fetchInvoiceSources(all.map((each) => each.id)),
    [all.map((each) => each.id).join(",")],
  );

  const contact = job.contact ?? null;
  const customerName = invoiceCustomerName(contact ?? {});

  const [billToName, setBillToName] = useState(customerName);
  const [billToEmail, setBillToEmail] = useState(contact?.email ?? "");
  const [billToAddress, setBillToAddress] = useState(
    invoiceAddressLines(contact ?? {}).join("\n"),
  );
  const [invoiceDay, setInvoiceDay] = useState(today());
  const [dueDay, setDueDay] = useState(invoiceDueDate(today()));
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<InvoiceRow | null>(null);
  const [emailing, setEmailing] = useState<InvoiceRow | null>(null);

  // The bill-to name is seeded from the customer, so it only becomes a
  // different party once somebody types over it. Until then the memo stays
  // empty rather than telling the customer their own name.
  const rows = invoices.data ?? [];
  const sequence = rows.length + 1;

  /**
   * One group per ticked estimate, each priced as a bill of its own.
   *
   * The open estimate falls back to the lines it arrived with, so the preview
   * is drawn before `fetchInvoiceSources` answers. Everything else waits for
   * its read — an estimate with no lines yet contributes nothing rather than a
   * wrong nothing, and the total below says so while it loads.
   */
  const groupFor = (each: Estimate): InvoiceGroup => {
    const source = sources.data?.get(each.id);
    const own = each.id === estimate.id;
    return {
      label: labels?.get(each.id) ?? each.name ?? null,
      items: source?.items ?? (own ? items : []),
      changeOrders: source?.changeOrders ?? (own ? changeOrders : []),
      officeDiscount: each.office_discount,
      jobType: job.job_type,
      gcFeeOverride:
        source?.adjustments.overrides.gc_fee ??
        (own ? adjustments.data?.overrides.gc_fee : undefined),
      gcFeeRemoved:
        source?.adjustments.removals.gc_fee ??
        (own ? adjustments.data?.removals.gc_fee : undefined),
    };
  };
  const groups: InvoiceGroup[] = chosen.map(groupFor);

  /** What one estimate would put on the bill, for the column beside its box. */
  const estimateTotal = (each: Estimate) =>
    invoiceTotal(invoiceLines(groupFor(each)));

  /*
   * The document as it stands. Built on every render rather than memoised: it
   * is arithmetic over a handful of lines, and the tick boxes above rebuild
   * their groups each time anyway — a dependency list over those would be a
   * list that has to be kept right for no gain.
   */
  const draft: InvoiceSnapshot = buildInvoice({
    number: invoiceNumber(job.job_number, sequence),
    invoiceDate: invoiceDay,
    dueDate: dueDay,
    company: {
      name: settings.data?.name ?? null,
      email: settings.data?.email ?? null,
      address: settings.data?.address ?? null,
    },
    customer: contact ?? {},
    billTo: {
      name: billToName,
      email: billToEmail,
      address: billToAddress
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    },
    memo,
    groups,
    jobType: job.job_type,
    // Straight off `jobBalance`, never a subtraction of this screen's own.
    received: balance?.collected ?? null,
    balanceDue: balance?.outstanding ?? null,
  });

  const chosenSourcesReady = chosen.every((each) => sources.data?.has(each.id));
  const readError = sources.error ?? balanceQuery.error;
  const blocker =
    chosenIds.length === 0
      ? t("Tick at least one estimate to bill.")
      : readError
        ? t("The figures for this invoice could not be read.")
        : !chosenSourcesReady || balance === null
          ? t("Reading the selected estimates and job balance…")
          : blockerOf(draft);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const row = await createInvoice({
        jobId: job.id,
        // The column holds one, and a bill covering several names the first of
        // them. What it actually billed is the document: each estimate is a
        // heading on the snapshot, and the snapshot is what is frozen.
        estimateId: chosenIds[0] ?? estimate.id,
        // Every selected estimate is recorded by the database. That is what
        // keeps a second unsent invoice from billing any of them again.
        estimateIds: chosenIds,
        invoiceDate: invoiceDay,
        dueDate: dueDay,
        snapshot: draft,
      });
      await invoices.reload();
      setMade(row);
      // Hand the file over straight away. Making one and then having to find a
      // second button to get the thing you came for is how a rep decides the
      // feature does not work.
      await downloadInvoicePdf(row.snapshot, invoiceFilename(row.number));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  if (emailing) {
    return (
      <EmailInvoiceModal
        job={job}
        customerEmail={contact?.email ?? null}
        row={emailing}
        onClose={() => setEmailing(null)}
        onSent={async () => {
          setEmailing(null);
          await invoices.reload();
        }}
      />
    );
  }

  return (
    <Modal
      title={t("Invoice")}
      subtitle={
        made
          ? t("Invoice {number} made — {amount}", {
              number: made.number,
              amount: invoiceMoney(made.total_cents),
            })
          : t("A PDF the customer, a carrier or a GC can pay off.")
      }
      onClose={onClose}
      actions={
        made ? (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() =>
                void downloadInvoicePdf(
                  made.snapshot,
                  invoiceFilename(made.number),
                )
              }
            >
              {t("Download the PDF again")}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEmailing(made)}
            >
              {t("Email it")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={busy}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => void create()}
              disabled={busy || blocker !== null}
              title={blocker ?? undefined}
            >
              {busy ? t("Making it…") : t("Make the invoice")}
            </button>
          </>
        )
      }
    >
      <ErrorNote error={error} />
      <ErrorNote error={readError} />

      {rows.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="subtle" style={{ marginBottom: 6 }}>
            {t("Already billed on this job")}
          </div>
          <div className="ewrap">
            <table className="etable">
              <tbody>
                {rows.map((row) => (
                  <InvoiceRowLine
                    key={row.id}
                    row={row}
                    onEmail={() => setEmailing(row)}
                    onVoid={async () => {
                      if (
                        !window.confirm(
                          t("Void invoice {number}?", { number: row.number }) +
                            "\n\n" +
                            t(
                              "The document is kept — voiding records that it no longer stands.",
                            ),
                        )
                      ) {
                        return;
                      }
                      await voidInvoice(row.id);
                      await invoices.reload();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!made && (
        <>
          {blocker && <div className="stub">{blocker}</div>}

          {all.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <div className="subtle" style={{ marginBottom: 6 }}>
                {t("What to bill")}
              </div>
              <div className="ewrap">
                <table className="etable">
                  <tbody>
                    {all.map((each) => {
                      const ticked = chosenIds.includes(each.id);
                      return (
                        <tr key={each.id}>
                          <td>
                            <label
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "baseline",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={ticked}
                                disabled={busy}
                                onChange={() =>
                                  setChosenIds((was) =>
                                    was.includes(each.id)
                                      ? was.filter((id) => id !== each.id)
                                      : all
                                          .filter(
                                            (e) =>
                                              e.id === each.id ||
                                              was.includes(e.id),
                                          )
                                          .map((e) => e.id),
                                  )
                                }
                              />
                              <span>
                                <b>
                                  {labels?.get(each.id) ??
                                    each.name ??
                                    `Estimate ${each.version ?? ""}`.trim()}
                                </b>
                                <div className="sub">
                                  {each.is_active ? t("Active") : t("Option")}
                                  {each.id === estimate.id ? t(" · open") : ""}
                                </div>
                              </span>
                            </label>
                          </td>
                          <td className="num">
                            {invoiceMoney(estimateTotal(each))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="fgrid">
            <div className="field">
              <label>{t("Bill to")}</label>
              <input
                value={billToName}
                onChange={(e) => setBillToName(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="field">
              <label>{t("Their email")}</label>
              <input
                type="email"
                value={billToEmail}
                onChange={(e) => setBillToEmail(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="fgrid one">
            <div className="field">
              <label>{t("Their address")}</label>
              <textarea
                rows={3}
                value={billToAddress}
                onChange={(e) => setBillToAddress(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="fgrid">
            <div className="field">
              <label>{t("Invoice date")}</label>
              <input
                type="date"
                value={invoiceDay}
                onChange={(e) => {
                  setInvoiceDay(e.target.value);
                  // The due date follows the invoice date unless somebody has
                  // moved it themselves — two days out, which is the term.
                  setDueDay(invoiceDueDate(e.target.value));
                }}
                disabled={busy}
              />
            </div>
            <div className="field">
              <label>{t("Due date")}</label>
              <input
                type="date"
                value={dueDay}
                onChange={(e) => setDueDay(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="fgrid one">
            <div className="field">
              <label>{t("Memo")}</label>
              <input
                value={memo}
                placeholder={draft.memo || t("Nothing")}
                onChange={(e) => setMemo(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          <div className="subtle" style={{ margin: "14px 0 6px" }}>
            {t("What it will say — invoice {number}, due {date}", {
              number: draft.number,
              date: fmtDateOnly(draft.dueDate),
            })}
          </div>
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Quantity</th>
                  <th className="num">Unit price</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((line, index) => (
                  <Fragment key={`${line.description}-${index}`}>
                    {line.group &&
                      line.group !== draft.lines[index - 1]?.group && (
                        <tr>
                          <td colSpan={4}>
                            <b>{line.group}</b>
                          </td>
                        </tr>
                      )}
                    <tr>
                      <td>{line.description}</td>
                      <td className="num">{invoiceQty(line.qty)}</td>
                      <td className="num">{invoiceMoney(line.unitPrice)}</td>
                      <td className="num">{invoiceMoney(line.amount)}</td>
                    </tr>
                  </Fragment>
                ))}
                <tr>
                  <td colSpan={3}>
                    <b>Total</b>
                  </td>
                  <td className="num">
                    <b>{invoiceMoney(draft.total)}</b>
                  </td>
                </tr>
                {/*
                 * What has been paid and what is left, on the bill itself.
                 * Both off `jobBalance`, so this can never disagree with the
                 * balance card a screen up or the letter that chases it.
                 */}
                {balance && (
                  <>
                    <tr>
                      <td colSpan={3}>Received to date</td>
                      <td className="num">{invoiceMoney(balance.collected)}</td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <b>Balance due</b>
                      </td>
                      <td className="num">
                        <b>{invoiceMoney(balance.outstanding)}</b>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          {balance &&
            balance.contract > 0 &&
            balance.contract !== draft.total && (
              <div className="sub" style={{ marginTop: 6 }}>
                {t(
                  "Received and balance are for the whole job ({contract}), which this invoice is part of.",
                  { contract: balance.contractFormatted },
                )}
              </div>
            )}
          {balance && balance.onItsWay > 0 && (
            <div className="sub" style={{ marginTop: 6 }}>
              {t(
                "{amount} of that balance has been paid and is still on its way to the bank.",
                { amount: balance.onItsWayFormatted },
              )}
            </div>
          )}
        </>
      )}

      {made && (
        <div className="stub">
          <b>
            {t("Invoice {number} is made and cannot be changed", {
              number: made.number,
            })}
          </b>
          {t(
            "The estimate can carry on being edited without touching it. If it is wrong, void it and make another — the next one takes the next number.",
          )}
        </div>
      )}
    </Modal>
  );
}

/** One bill already on the job, and what can still be done with it. */
function InvoiceRowLine({
  row,
  onEmail,
  onVoid,
}: {
  row: InvoiceRow;
  onEmail: () => void;
  onVoid: () => Promise<void>;
}) {
  const voided = row.status === "void";
  return (
    <tr>
      <td>
        <b style={{ textDecoration: voided ? "line-through" : undefined }}>
          {row.number}
        </b>{" "}
        {invoiceMoney(row.total_cents)}
        <div className="sub">
          {voided
            ? t("Voided")
            : row.status === "sent"
              ? row.sent_to
                ? t("Sent {date} to {to}", {
                    date: fmtDate(row.sent_at ?? row.created_at),
                    to: row.sent_to,
                  })
                : t("Sent {date}", {
                    date: fmtDate(row.sent_at ?? row.created_at),
                  })
              : t("Made {date} · not sent", { date: fmtDate(row.created_at) })}
          {t(" · due {date}", { date: fmtDateOnly(row.due_date) })}
        </div>
      </td>
      <td className="rowacts">
        <button
          type="button"
          className="btn-small"
          onClick={() =>
            void downloadInvoicePdf(row.snapshot, invoiceFilename(row.number))
          }
        >
          PDF
        </button>
        {!voided && (
          <>
            <button type="button" className="btn-small" onClick={onEmail}>
              {t("Email")}
            </button>
            <button
              type="button"
              className="btn-small"
              onClick={() => void onVoid()}
            >
              {t("Void")}
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

/** Send it, with the PDF hanging off the message. */
function EmailInvoiceModal({
  job,
  customerEmail,
  row,
  onClose,
  onSent,
}: {
  job: Job;
  /** So the follow-up clock can tell a customer from a carrier. */
  customerEmail: string | null;
  row: InvoiceRow;
  onClose: () => void;
  onSent: () => Promise<void>;
}) {
  const { account } = useGmailAccount();
  const { profileId } = useSession();
  const where = jobAddress(job) || "your property";

  const [to, setTo] = useState(row.snapshot.billTo.email ?? "");
  const [subject, setSubject] = useState(`Invoice ${row.number} — ${where}`);
  const [message, setMessage] = useState(
    `Attached is invoice ${row.number} for ${invoiceMoney(row.total_cents)}, due ${fmtDateOnly(
      row.due_date,
    )}.\n\n` +
      `${row.snapshot.memo ? `${row.snapshot.memo}\n\n` : ""}` +
      `Please let us know if you need anything else to process it.\n`,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const content = await invoicePdfBase64(row.snapshot);
      await sendEmail({
        to: to.trim(),
        subject: subject.trim(),
        text: message,
        jobId: job.id,
        contactId: job.contact_id ?? null,
        // A carrier or a GC is not the customer, so this must not tick the
        // follow-up clock that counts contact with the homeowner. When the
        // homeowner *is* the one being billed, it should.
        internal:
          to.trim().toLowerCase() !==
          (customerEmail ?? "").trim().toLowerCase(),
        attachments: [
          {
            filename: invoiceFilename(row.number),
            mimeType: "application/pdf",
            content,
          },
        ],
      });
      await markInvoiceSent(row.id, to.trim());
      /*
       * And onto the Docs card, with the same bytes that were attached rather
       * than a fresh render — a second render of the same snapshot ought to be
       * identical and "ought to" is not what a customer's invoice runs on.
       * Jonathon, 2026-08-28: any PDF on the job belongs in Docs.
       *
       * After the send, and not fatal to it: an invoice that reached the
       * customer and failed to file is a filing problem, and throwing here
       * would tell the rep it had not been sent.
       */
      try {
        await fileEmailedPdf({
          jobId: job.id,
          kind: "invoice",
          title: `Invoice ${row.number}`,
          filename: invoiceFilename(row.number),
          base64: content,
          emailedTo: to.trim(),
          profileId: profileId ?? null,
        });
      } catch {
        // Filed or not, it was sent. The rep is told about send failures only.
      }
      await onSent();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t("Email invoice {number}", { number: row.number })}
      subtitle={t("The PDF goes with it.")}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void send()}
            disabled={busy || !to.trim() || !subject.trim() || !account}
          >
            {busy ? t("Sending…") : t("Send")}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />
      {!account && (
        <div className="stub">
          <b>{t("Your Gmail is not connected")}</b>
          {t(
            "Nothing can be sent from here until it is — Tools → Integrations. The PDF can still be downloaded and attached from your own mail.",
          )}
        </div>
      )}
      <div className="fgrid one">
        <div className="field">
          <label>{t("To")}</label>
          <input
            type="email"
            value={to}
            placeholder="billing@example.com"
            onChange={(e) => setTo(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Subject")}</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label>{t("Message")}</label>
          <textarea
            rows={8}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
    </Modal>
  );
}
